import i18n from '@/i18n';

import { APP_INSTANCE_USAGE_SCENARIO_OPTIONS, UsageScenario, getProtocolsFromUsageScenario, resolveUsageScenario } from './constants';
import type { ServiceFormItem } from './formTypes';

const t = i18n.t.bind(i18n);

/** 不可重复添加的使用场景 */
export const UNIQUE_USAGE_SCENARIOS = new Set<string>([UsageScenario.OPENAI, UsageScenario.ANTHROPIC, UsageScenario.SSH]);

export { APP_INSTANCE_USAGE_SCENARIO_OPTIONS as DEFAULT_USAGE_SCENARIO_OPTIONS };

/** 根据使用场景同步 applicationProtocol / protocol（传输层） */
export function applyUsageScenarioToServiceItem(item: ServiceFormItem): ServiceFormItem {
  const usageScenario = resolveUsageScenario(item.usageScenario);
  if (!usageScenario) {
    return {
      ...item,
      usageScenario: undefined,
      applicationProtocol: item.applicationProtocol,
      protocol: item.protocol,
    };
  }
  const { protocol, tranProtocol } = getProtocolsFromUsageScenario(usageScenario);
  return {
    ...item,
    usageScenario,
    applicationProtocol: protocol || undefined,
    protocol: tranProtocol,
  };
}

/** 从 legacy applicationProtocol / protocol 推断 usageScenario（镜像预置、旧数据兼容） */
export function inferUsageScenarioFromLegacyItem(item: ServiceFormItem): UsageScenario | undefined {
  const scenarioFromField = resolveUsageScenario(item.usageScenario);
  if (scenarioFromField) return scenarioFromField;

  const appProtocol = String(item.applicationProtocol || '').trim();
  const transport = String(item.protocol || '')
    .trim()
    .toUpperCase();
  const name = String(item.name || '')
    .trim()
    .toLowerCase();

  if (appProtocol === 'OpenAI') return UsageScenario.OPENAI;
  if (appProtocol === 'Anthropic') return UsageScenario.ANTHROPIC;
  if (appProtocol === 'SSH' || name === 'ssh') return UsageScenario.SSH;
  if (transport === 'UDP') return UsageScenario.UDP;
  if (appProtocol === 'HTTP' || appProtocol === 'HTTPS') {
    return appProtocol === 'HTTPS' ? UsageScenario.HTTPS : UsageScenario.HTTP;
  }
  if (transport === 'TCP') return UsageScenario.TCP;
  return undefined;
}

export function normalizeServiceItem(item: ServiceFormItem): ServiceFormItem {
  const usageScenario = inferUsageScenarioFromLegacyItem(item) ?? item.usageScenario;
  return applyUsageScenarioToServiceItem({ ...item, usageScenario });
}

export function getUniqueUsageScenarioError(list: ServiceFormItem[]): string | undefined {
  const counts = new Map<string, number>();
  list.forEach((item) => {
    const scenario = String(item.usageScenario || '').trim();
    if (!scenario || !UNIQUE_USAGE_SCENARIOS.has(scenario)) return;
    counts.set(scenario, (counts.get(scenario) || 0) + 1);
  });

  const duplicated = Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([scenario]) => scenario);

  if (!duplicated.length) return undefined;
  return t('data-center:maas.service.protocol.unique', { protocols: duplicated.join(' / ') });
}

export function getDuplicateServiceNameError(list: ServiceFormItem[]): string | undefined {
  const counts = new Map<string, number>();
  list.forEach((item) => {
    const name = String(item.name || '').trim();
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });

  const duplicated = Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([name]) => name);

  if (!duplicated.length) return undefined;
  return t('data-center:maas.service.name.unique', { names: duplicated.join(' / ') });
}

/** 校验服务内部端口唯一，避免多条服务监听同一端口。 */
export function getDuplicateServicePortError(list: ServiceFormItem[]): string | undefined {
  const ports = list.map((item) => Number(item.internalPort)).filter((port) => Number.isFinite(port) && port > 0);
  if (new Set(ports).size === ports.length) return undefined;
  return t('cluster:service-internal-port-duplicate');
}
